import React, { useState, useRef } from "react";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { GanttChartSquare, Flag, CornerDownRight, ChevronDown, ChevronRight, ChevronLeft, Plus, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import HScroll from "./HScroll";

const DAY = 86400000;
const parseD = (s) => { if (!s || typeof s !== "string") return null; const d = new Date(`${s}T00:00:00`); return isNaN(d.getTime()) ? null : d; };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const diffDays = (a, b) => Math.round((a.getTime() - b.getTime()) / DAY);
const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfWeek = (d) => { const x = midnight(d); return addDays(x, -((x.getDay() + 6) % 7)); };
const ROW_H = 44;
const BAR_CY = 22; // vertical center of every bar within its row

const ZOOM = { day: { dw: 34, pad: 2 }, week: { dw: 15, pad: 7 }, month: { dw: 6, pad: 20 }, quarter: { dw: 2.8, pad: 60 }, year: { dw: 1.2, pad: 150 } };
const eachStep = (s, e, next) => { const a = []; let d = s; while (d <= e) { a.push(new Date(d)); d = next(d); } return a; };
const stepMonth = (s, e) => eachStep(new Date(s.getFullYear(), s.getMonth(), 1), e, (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
const stepDay = (s, e) => eachStep(midnight(s), e, (d) => addDays(d, 1));
const stepWeek = (s, e) => eachStep(startOfWeek(s), e, (d) => addDays(d, 7));
const stepQuarter = (s, e) => eachStep(new Date(s.getFullYear(), Math.floor(s.getMonth() / 3) * 3, 1), e, (d) => new Date(d.getFullYear(), d.getMonth() + 3, 1));
const stepYear = (s, e) => eachStep(new Date(s.getFullYear(), 0, 1), e, (d) => new Date(d.getFullYear() + 1, 0, 1));

export default function GanttView({ board, milestones = [], onSetCells, onSetDeps, onOpen, onAddItem, onAddSubitem }) {
  const { t, lang } = useApp();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const [zoom, setZoom] = useState("day");
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [drag, setDrag] = useState(null);
  const [linking, setLinking] = useState(null);
  const session = useRef(null);
  const chartRef = useRef(null);
  const scrollRef = useRef(null);
  const todayXRef = useRef(0);
  const { dw, pad } = ZOOM[zoom];

  const panBy = (dir) => { const el = scrollRef.current; if (el) el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.7), behavior: "smooth" }); };
  const scrollToToday = () => { const el = scrollRef.current; if (el) el.scrollTo({ left: Math.max(0, todayXRef.current - el.clientWidth / 2), behavior: "smooth" }); };

  const endIdFor = (isSub) => (isSub ? "date" : "due");
  const rangeFor = (item, isSub) => {
    const s = parseD(item.values?.start), e = parseD(item.values?.[endIdFor(isSub)]);
    if (!s && !e) return null;
    const a = s || e, b = e || s;
    return { start: a <= b ? a : b, end: a <= b ? b : a };
  };

  const tops = board.items.filter((i) => !i.parent_id).sort((a, b) => (a.position || 0) - (b.position || 0));
  const subsOf = (id) => board.items.filter((s) => s.parent_id === id).sort((a, b) => (a.position || 0) - (b.position || 0));
  const allTopWithSubs = tops.filter((it) => subsOf(it.id).length > 0).map((it) => it.id);

  const rows = [];
  tops.forEach((it) => {
    const subs = subsOf(it.id);
    const color = board.groups.find((g) => g.id === it.group_id)?.color || "#3B82F6";
    rows.push({ item: it, isSub: false, hasSubs: subs.length > 0, range: rangeFor(it, false), color });
    if (!collapsed.has(it.id)) subs.forEach((s) => rows.push({ item: s, isSub: true, hasSubs: false, range: rangeFor(s, true), color }));
  });
  const idxOf = {}, rowById = {};
  rows.forEach((r, i) => { idxOf[r.item.id] = i; rowById[r.item.id] = r; });

  const validMilestones = (milestones || []).map((m) => ({ ...m, d: parseD(m.target_date) })).filter((m) => m.d);

  const toolbar = (
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <div className="flex items-center gap-2">
        <button onClick={onAddItem} data-testid="gantt-add-item" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors shadow-sm"><Plus className="w-3.5 h-3.5" />{t("gantt_add_item")}</button>
        {allTopWithSubs.length > 0 && (
          collapsed.size >= allTopWithSubs.length
            ? <button onClick={() => setCollapsed(new Set())} data-testid="gantt-expand-all" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50"><ChevronsUpDown className="w-3.5 h-3.5" />{t("expand_all")}</button>
            : <button onClick={() => setCollapsed(new Set(allTopWithSubs))} data-testid="gantt-collapse-all" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50"><ChevronsDownUp className="w-3.5 h-3.5" />{t("collapse_all")}</button>
        )}
      </div>
      <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5">
        {["day", "week", "month", "quarter", "year"].map((z) => (
          <button key={z} onClick={() => setZoom(z)} data-testid={`gantt-zoom-${z}`}
            className={cn("px-3 py-1 rounded-md text-xs font-semibold transition-colors", zoom === z ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700")}>{t(`zoom_${z}`)}</button>
        ))}
      </div>
      <div className="inline-flex items-center gap-1">
        <button onClick={() => panBy(-1)} data-testid="gantt-pan-left" title={t("gantt_pan_left")} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <button onClick={scrollToToday} data-testid="gantt-today-btn" className="px-2.5 h-8 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50 transition-colors">{t("board_today")}</button>
        <button onClick={() => panBy(1)} data-testid="gantt-pan-right" title={t("gantt_pan_right")} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center transition-colors"><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );

  if (!rows.length) {
    return <div>{toolbar}<div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-400" data-testid="gantt-empty"><GanttChartSquare className="w-7 h-7 mx-auto mb-3 opacity-50" />{t("board_gantt_no_dates")}</div></div>;
  }

  const dated = rows.filter((r) => r.range);
  const today = midnight(new Date());
  const allDates = [...dated.flatMap((r) => [r.range.start, r.range.end]), ...validMilestones.map((m) => m.d), today];
  const min = new Date(Math.min(...allDates.map((d) => d.getTime())));
  const max = new Date(Math.max(...allDates.map((d) => d.getTime())));
  const rangeStart = addDays(midnight(min), -pad);
  const rangeEnd = addDays(midnight(max), pad);
  const totalDays = Math.max(1, diffDays(rangeEnd, rangeStart));
  const chartWidth = Math.max(totalDays * dw, 600);
  const x = (d) => diffDays(midnight(d), rangeStart) * dw;

  const TIER = {
    day: { top: stepMonth, sub: stepDay, topFmt: (d) => d.toLocaleDateString(loc, { month: "long", year: "numeric" }), subFmt: (d) => String(d.getDate()) },
    week: { top: stepMonth, sub: stepWeek, topFmt: (d) => d.toLocaleDateString(loc, { month: "long", year: "numeric" }), subFmt: (d) => d.toLocaleDateString(loc, { day: "numeric", month: "short" }) },
    month: { top: stepYear, sub: stepMonth, topFmt: (d) => String(d.getFullYear()), subFmt: (d) => d.toLocaleDateString(loc, { month: "short" }) },
    quarter: { top: stepYear, sub: stepQuarter, topFmt: (d) => String(d.getFullYear()), subFmt: (d) => `Q${Math.floor(d.getMonth() / 3) + 1}` },
    year: { top: stepYear, sub: stepQuarter, topFmt: (d) => String(d.getFullYear()), subFmt: (d) => `Q${Math.floor(d.getMonth() / 3) + 1}` },
  }[zoom];
  const topTicks = TIER.top(rangeStart, rangeEnd);
  const subTicks = TIER.sub(rangeStart, rangeEnd);

  const geom = (row) => {
    let left = x(row.range.start);
    let width = Math.max(dw, (diffDays(midnight(row.range.end), midnight(row.range.start)) + 1) * dw);
    if (drag && drag.id === row.item.id) {
      const off = drag.days * dw;
      if (drag.mode === "move") left += off;
      else if (drag.mode === "l") { left += off; width -= off; }
      else if (drag.mode === "r") width += off;
      width = Math.max(dw, width);
    }
    return { left, width };
  };

  // ---- dependency auto-shift: push dependents forward so they start after their predecessors finish ----
  const enforce = (overrides = {}, depsOf = (it) => it.deps || []) => {
    const map = {};
    board.items.forEach((it) => { const r = rangeFor(it, !!it.parent_id); if (r) map[it.id] = { start: r.start, end: r.end, isSub: !!it.parent_id }; });
    Object.entries(overrides).forEach(([id, o]) => { if (map[id]) map[id] = { ...map[id], start: o.start, end: o.end }; });
    const changed = {}; let dirty = true, pass = 0;
    while (dirty && pass++ < 40) {
      dirty = false;
      board.items.forEach((S) => {
        const d = depsOf(S); if (!d.length || !map[S.id]) return;
        d.forEach((pid) => {
          const P = map[pid]; if (!P) return;
          const minS = addDays(P.end, 1);
          if (map[S.id].start < minS) { const dur = diffDays(map[S.id].end, map[S.id].start); map[S.id].start = minS; map[S.id].end = addDays(minS, dur); changed[S.id] = true; dirty = true; }
        });
      });
    }
    Object.keys(changed).forEach((id) => { const it = board.items.find((i) => i.id === id); if (it) onSetCells(it, { start: iso(map[id].start), [endIdFor(map[id].isSub)]: iso(map[id].end) }); });
  };

  const reaches = (startId, targetId) => {
    const seen = new Set(); const stack = [startId];
    while (stack.length) { const cur = stack.pop(); if (cur === targetId) return true; if (seen.has(cur)) continue; seen.add(cur); const it = board.items.find((i) => i.id === cur); (it?.deps || []).forEach((p) => stack.push(p)); }
    return false;
  };
  const addDep = (succId, predId) => {
    if (succId === predId) return;
    const succ = board.items.find((i) => i.id === succId); if (!succ) return;
    const deps = succ.deps || []; if (deps.includes(predId)) return;
    if (reaches(predId, succId)) return; // would create a cycle
    const next = [...deps, predId];
    onSetDeps(succ, next);
    enforce({}, (it) => (it.id === succId ? next : it.deps || []));
  };
  const removeDep = (succId, predId) => { const succ = board.items.find((i) => i.id === succId); if (succ) onSetDeps(succ, (succ.deps || []).filter((d) => d !== predId)); };

  // ---- drag to reschedule (move / resize) ----
  const startDrag = (e, row, mode) => {
    e.preventDefault(); e.stopPropagation();
    const sess = { id: row.item.id, isSub: row.isSub, mode, startX: e.clientX, range: row.range, days: 0 };
    session.current = sess;
    const onMove = (ev) => { sess.days = Math.round((ev.clientX - sess.startX) / dw); setDrag({ id: sess.id, mode, days: sess.days }); };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove); document.removeEventListener("pointerup", onUp);
      setDrag(null); session.current = null;
      if (!sess.days) return;
      const item = board.items.find((i) => i.id === sess.id); if (!item) return;
      let ns = sess.range.start, ne = sess.range.end;
      if (mode === "move") { ns = addDays(ns, sess.days); ne = addDays(ne, sess.days); }
      else if (mode === "l") { ns = addDays(ns, sess.days); if (ns > ne) ns = ne; }
      else if (mode === "r") { ne = addDays(ne, sess.days); if (ne < ns) ne = ns; }
      onSetCells(item, { start: iso(ns), [endIdFor(sess.isSub)]: iso(ne) });
      enforce({ [item.id]: { start: ns, end: ne } });
    };
    document.addEventListener("pointermove", onMove); document.addEventListener("pointerup", onUp);
  };

  // ---- create a dependency by dragging from a bar's link handle onto another bar ----
  const startLink = (e, fromRow) => {
    e.preventDefault(); e.stopPropagation();
    const rect = chartRef.current?.getBoundingClientRect();
    const upd = (ev) => { if (rect) setLinking({ fromId: fromRow.item.id, x: ev.clientX - rect.left, y: ev.clientY - rect.top }); };
    upd(e);
    const onMove = (ev) => upd(ev);
    const onUp = (ev) => {
      document.removeEventListener("pointermove", onMove); document.removeEventListener("pointerup", onUp);
      setLinking(null);
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const barEl = el && el.closest && el.closest('[data-testid^="gantt-bar-"]');
      if (barEl) addDep(barEl.getAttribute("data-testid").replace("gantt-bar-", ""), fromRow.item.id);
    };
    document.addEventListener("pointermove", onMove); document.addEventListener("pointerup", onUp);
  };

  const arrowGeo = (predR, succR) => {
    const a = geom(predR), b = geom(succR);
    const x1 = a.left + a.width, y1 = idxOf[predR.item.id] * ROW_H + BAR_CY;
    const x2 = b.left, y2 = idxOf[succR.item.id] * ROW_H + BAR_CY;
    const gap = 11, violated = x2 < x1;
    let d;
    if (x2 >= x1 + gap) d = `M ${x1} ${y1} H ${x2 - gap} V ${y2} H ${x2}`;
    else { const my = (y2 > y1 ? y1 + ROW_H / 2 : y1 - ROW_H / 2); d = `M ${x1} ${y1} H ${x1 + gap} V ${my} H ${x2 - gap} V ${y2} H ${x2}`; }
    return { d, x2, y2, violated };
  };
  const arrows = [];
  rows.forEach((succR) => { if (!succR.range) return; (succR.item.deps || []).forEach((pid) => { const predR = rowById[pid]; if (predR && predR.range) arrows.push({ predR, succR }); }); });

  const bodyH = rows.length * ROW_H;
  const todayIn = today >= rangeStart && today <= rangeEnd;
  todayXRef.current = x(today);
  const linkSrc = linking && rowById[linking.fromId];

  return (
    <div data-testid="gantt-view">
      {toolbar}
      <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm bg-white text-slate-800">
        <div className="flex">
          {/* Left: item names */}
          <div className="w-[280px] shrink-0 border-r border-slate-200 bg-slate-50/80">
            <div className="h-14 border-b border-slate-200 flex items-end px-4 pb-2.5"><span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{t("board_tasks")}</span></div>
            {rows.map((r) => (
              <div key={r.item.id} style={{ height: ROW_H }} className={cn("group/gr border-b border-slate-100 last:border-0 flex items-center gap-1", r.isSub ? "pl-8 pr-3 bg-slate-50/60" : "pl-3 pr-3")}>
                {!r.isSub && r.hasSubs ? (
                  <button onClick={() => setCollapsed((s) => { const n = new Set(s); n.has(r.item.id) ? n.delete(r.item.id) : n.add(r.item.id); return n; })} data-testid={`gantt-toggle-${r.item.id}`} className="shrink-0 text-slate-400 hover:text-slate-700">
                    {collapsed.has(r.item.id) ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                ) : <span className="w-4 shrink-0">{r.isSub && <CornerDownRight className="w-3 h-3 text-slate-300" />}</span>}
                <button onClick={() => onOpen(r.item.id)} data-testid={`gantt-name-${r.item.id}`} className={cn("truncate text-left hover:text-blue-600 transition-colors flex-1", r.isSub ? "text-xs text-slate-500" : "text-sm font-medium text-slate-700")}>{r.item.name}</button>
                {!r.isSub && <button onClick={() => onAddSubitem(r.item)} data-testid={`gantt-addsub-${r.item.id}`} title={t("board_add_subitem")} className="shrink-0 opacity-0 group-hover/gr:opacity-100 text-slate-400 hover:text-blue-600 transition-all"><Plus className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
          </div>

          {/* Right: scrollable timeline */}
          <HScroll innerClassName="flex-1" testid="gantt-hscroll" contentRef={scrollRef}>
            <div ref={chartRef} style={{ width: chartWidth }} className="relative">
              {/* Header */}
              <div className="h-14 border-b border-slate-200 relative bg-slate-50/80">
                <div className="absolute inset-x-0 top-0 h-7 border-b border-slate-100">
                  {topTicks.map((d) => <div key={+d} className="absolute top-0 h-7 flex items-center pl-2 text-[11px] font-bold text-slate-600 border-l border-slate-200 capitalize whitespace-nowrap" style={{ left: Math.max(0, x(d)) }}>{TIER.topFmt(d)}</div>)}
                </div>
                <div className="absolute inset-x-0 bottom-0 h-7">
                  {subTicks.map((d) => <div key={+d} className="absolute bottom-0 h-7 flex items-center justify-center text-[10px] font-medium text-slate-400 border-l border-slate-100" style={{ left: Math.max(0, x(d)), width: dw * (zoom === "week" ? 7 : 1) }}>{dw >= 11 || zoom !== "day" ? TIER.subFmt(d) : ""}</div>)}
                </div>
              </div>
              <div className="absolute left-0 right-0 top-0 h-7 pointer-events-none z-20">
                {validMilestones.map((m) => (
                  <div key={m.id} className="absolute top-1 -translate-x-1/2 pointer-events-auto" style={{ left: x(m.d) }} data-testid={`gantt-milestone-${m.id}`}>
                    <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold whitespace-nowrap shadow text-white", m.status === "done" ? "bg-emerald-600" : "bg-amber-500")} title={m.name}><Flag className="w-2.5 h-2.5" />{m.name}</span>
                  </div>
                ))}
              </div>

              {/* Body */}
              <div className="relative" style={{ height: bodyH }}>
                {subTicks.map((d) => <div key={+d} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: Math.max(0, x(d)) }} />)}
                {rows.map((r, i) => i % 2 === 1 && <div key={`z${r.item.id}`} className="absolute inset-x-0 bg-slate-50/50" style={{ top: i * ROW_H, height: ROW_H }} />)}
                {validMilestones.map((m) => <div key={m.id} className={cn("absolute top-0 bottom-0 w-px", m.status === "done" ? "bg-emerald-400/60" : "bg-amber-400/70")} style={{ left: x(m.d) }} />)}
                {todayIn && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-blue-600 z-10" style={{ left: x(today) }} data-testid="gantt-today">
                    <span className="absolute -top-px left-1 text-[9px] font-bold text-white bg-blue-600 px-1 rounded-b">{t("board_today")}</span>
                  </div>
                )}
                {/* Bars */}
                {rows.map((r, i) => {
                  if (!r.range) return null;
                  const g = geom(r);
                  const dragging = drag && drag.id === r.item.id;
                  const labelInside = g.width >= 64;
                  return (
                    <div key={r.item.id} className="absolute inset-x-0" style={{ top: i * ROW_H, height: ROW_H }}>
                      <div data-testid={`gantt-bar-${r.item.id}`} onPointerDown={(e) => startDrag(e, r, "move")} title={`${r.item.name}\n${iso(r.range.start)} → ${iso(r.range.end)}`}
                        className={cn("group/bar absolute rounded-md flex items-center shadow-sm cursor-grab active:cursor-grabbing select-none", r.isSub && "opacity-90", dragging && "ring-2 ring-blue-400 shadow-lg z-20")}
                        style={{ left: g.left, width: g.width, top: r.isSub ? 14 : 10, height: r.isSub ? ROW_H - 28 : ROW_H - 20, backgroundColor: r.color }}>
                        <span onPointerDown={(e) => startDrag(e, r, "l")} data-testid={`gantt-resize-l-${r.item.id}`} className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize rounded-l-md opacity-0 group-hover/bar:opacity-100 bg-black/20" />
                        {labelInside && <span className="px-2 text-[11px] font-semibold text-white truncate pointer-events-none">{r.item.name}</span>}
                        <span onPointerDown={(e) => startDrag(e, r, "r")} data-testid={`gantt-resize-r-${r.item.id}`} className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize rounded-r-md opacity-0 group-hover/bar:opacity-100 bg-black/20" />
                        {/* link handle */}
                        <span onPointerDown={(e) => startLink(e, r)} data-testid={`gantt-link-${r.item.id}`} title={t("gantt_link_hint")}
                          className="absolute top-1/2 -right-2.5 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white border-2 opacity-0 group-hover/bar:opacity-100 cursor-crosshair z-30 hover:scale-125 transition-transform" style={{ borderColor: r.color }} />
                      </div>
                      {!labelInside && <span className="absolute text-[11px] font-medium text-slate-600 truncate pointer-events-none" style={{ left: g.left + g.width + 14, top: r.isSub ? 15 : 12, maxWidth: 220 }}>{r.item.name}</span>}
                    </div>
                  );
                })}
                {/* Dependency arrows overlay */}
                <svg className="absolute inset-0 z-[25]" width={chartWidth} height={bodyH} style={{ pointerEvents: "none" }}>
                  {arrows.map(({ predR, succR }, k) => {
                    const ag = arrowGeo(predR, succR);
                    const col = ag.violated ? "#EF4444" : "#94A3B8";
                    return (
                      <g key={`${predR.item.id}-${succR.item.id}-${k}`} className="group/arrow">
                        <path d={ag.d} fill="none" stroke="transparent" strokeWidth={12} style={{ pointerEvents: "stroke", cursor: "pointer" }} onClick={() => removeDep(succR.item.id, predR.item.id)} data-testid={`gantt-dep-${predR.item.id}-${succR.item.id}`}><title>{t("gantt_remove_dep")}</title></path>
                        <path d={ag.d} fill="none" stroke={col} strokeWidth={1.75} className="group-hover/arrow:stroke-blue-600" style={{ pointerEvents: "none" }} />
                        <polygon points={`${ag.x2 - 7},${ag.y2 - 4} ${ag.x2},${ag.y2} ${ag.x2 - 7},${ag.y2 + 4}`} fill={col} className="group-hover/arrow:fill-blue-600" style={{ pointerEvents: "none" }} />
                      </g>
                    );
                  })}
                  {linkSrc && linkSrc.range && (() => { const a = geom(linkSrc); const x1 = a.left + a.width, y1 = idxOf[linkSrc.item.id] * ROW_H + BAR_CY; return <line x1={x1} y1={y1} x2={linking.x} y2={linking.y} stroke="#2563EB" strokeWidth={2} strokeDasharray="4 3" style={{ pointerEvents: "none" }} />; })()}
                </svg>
              </div>
            </div>
          </HScroll>
        </div>
      </div>
      <p className="mt-2.5 text-[11px] text-slate-400 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-1.5 rounded bg-blue-600" />{t("board_today")}</span>
        <span>{t("board_gantt_hint")}</span>
        <span>{t("gantt_link_hint")}</span>
      </p>
    </div>
  );
}
